import { orgAPI } from "@/api/api";
import { useQuery } from "@tanstack/react-query";

interface UseGranolaTranscriptFetchProps {
  integrationConnectionId?: string;
  meetingId: string;
  /**
   * Gate the fetch. Pass `false` until the transcript tab is selected so we
   * don't pull the (potentially large) verbatim transcript on pane open.
   */
  enabled?: boolean;
}

// granola-mcp-get-meeting-transcript returns the raw MCP payload, which may be a
// plain VTT string, a { transcript } object, or { transcript: string[] }. Reduce
// it to a single displayable string.
function parseTranscript(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === "string") return raw.trim() || null;
  if (typeof raw === "object") {
    const t = (raw as { transcript?: unknown }).transcript;
    if (typeof t === "string") return t.trim() || null;
    if (Array.isArray(t)) {
      const joined = t
        .filter((x): x is string => typeof x === "string")
        .join("\n")
        .trim();
      return joined || null;
    }
  }
  return null;
}

export function useGranolaTranscriptFetch({
  integrationConnectionId,
  meetingId,
  enabled = true,
}: UseGranolaTranscriptFetchProps) {
  return useQuery({
    queryKey: ["granola-transcript", integrationConnectionId, meetingId],
    queryFn: async () => {
      if (!integrationConnectionId) {
        throw new Error("Missing integration connection ID");
      }
      const response = await orgAPI.toolbox.execute({
        integration_connection_id: integrationConnectionId,
        action_name: "granola-mcp-get-meeting-transcript",
        action_input: { meeting_id: meetingId },
      });
      return parseTranscript(response.data);
    },
    enabled: !!integrationConnectionId && !!meetingId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
